import { Filesystem, Directory } from "@capacitor/filesystem";
import { Capacitor } from "@capacitor/core";
import type { ProgressiveVideo } from "@/api/vimeo.types";
import {
	generateVideoId,
	upsertOfflineVideo,
	updateDownloadProgress,
	getOfflineVideo,
	type OfflineVideoMetadata,
} from "./storage/offlineVideosStorage";
import {
	saveCheckpoint,
	loadCheckpoint,
	clearCheckpoint,
} from "./storage/downloadCheckpoints";
import { videoLog } from "@/components/VideoDownloadDebugger"; // ✅ DODAJ
import type { ProductDataMultimedia, ProductTypeMultimedia } from "@/pages/Workshop/MultimediaCard";

// Cache for blob URLs (web platform only)
const videoBlobCache = new Map<string, string>();

/**
 * Clear blob URL cache (useful for memory management)
 */
export const clearVideoBlobCache = (): void => {
	// Revoke all blob URLs to free memory
	videoBlobCache.forEach((blobUrl) => {
		URL.revokeObjectURL(blobUrl);
	});
	videoBlobCache.clear();
	console.log("🧹 [Web] Video blob cache cleared");
};

/**
 * Clear specific video from cache
 */
export const clearVideoBlobFromCache = (videoId: string): void => {
	const blobUrl = videoBlobCache.get(videoId);
	if (blobUrl) {
		URL.revokeObjectURL(blobUrl);
		videoBlobCache.delete(videoId);
		console.log("🧹 [Web] Cleared video from cache:", videoId);
	}
};

/**
 * VIDEO DOWNLOAD SERVICE - Current Implementation
 * 
 * Uses JavaScript Fetch API + FileReader for downloading videos on native platforms.
 * Works for files up to ~200-300MB.
 * 
 * For future native implementation with background download support,
 * see: /docs/OFFLINE_VIDEO_NATIVE_PLUGIN.md
 */

/**
 * Helper function to convert Blob to Base64 using FileReader
 * More memory efficient than manual conversion for large files
 */
function blobToBase64(blob: Blob): Promise<string> {
	return new Promise<string>((resolve, reject) => {
		const reader = new FileReader();
		reader.onloadend = (): void => {
			if (typeof reader.result === "string") {
				// Remove the data URL prefix (e.g., "data:video/mp4;base64,")
				const base64 = reader.result.split(",")[1];
				if (base64) {
					resolve(base64);
				} else {
					reject(new Error("Failed to extract base64 from result"));
				}
			} else {
				reject(new Error("Failed to convert blob to base64"));
			}
		};
		reader.onerror = (): void => {
			reject(new Error("FileReader error"));
		};
		reader.readAsDataURL(blob);
	});
}

/**
 * Write large file in chunks to avoid Android bridge size limits
 * Android has a ~1MB limit for data passed through the JS-Java bridge
 */
async function writeFileInChunks(
	blob: Blob,
	filePath: string,
	videoId: string,
	onProgress?: (progress: number) => void
): Promise<void> {
	const CHUNK_SIZE = 512 * 1024; // 512KB chunks (safe for Android bridge)
	const totalSize = blob.size;
	let offset = 0;

	console.log(`📝 Writing file in chunks: ${totalSize} bytes`);
	videoLog.info("FileWrite", `Writing file in chunks: ${totalSize} bytes`, { 
		totalSize, 
		chunkSize: CHUNK_SIZE,
		estimatedChunks: Math.ceil(totalSize / CHUNK_SIZE)
	}, videoId); // ✅ DODAJ

	// Delete existing file if it exists
	try {
		await Filesystem.deleteFile({
			path: filePath,
			directory: Directory.Data,
		});
		videoLog.info("FileWrite", "Deleted existing file before write", { filePath }, videoId); // ✅ DODAJ
	} catch {
		// File doesn't exist, that's ok
	}

	while (offset < totalSize) {
		const chunk = blob.slice(offset, offset + CHUNK_SIZE);
		const base64Chunk = await blobToBase64(chunk);

		// First chunk: create file, subsequent chunks: append
		if (offset === 0) {
			await Filesystem.writeFile({
				path: filePath,
				data: base64Chunk,
				directory: Directory.Data,
			});
		} else {
			await Filesystem.appendFile({
				path: filePath,
				data: base64Chunk,
				directory: Directory.Data,
			});
		}

		offset += CHUNK_SIZE;
		
		// Calculate write progress (90% -> 100%)
		const writeProgress = Math.min((offset / totalSize) * 100, 100);
		const totalProgress = 90 + (writeProgress * 0.1); // Map 0-100% write to 90-100% total
		
		// Update progress in storage and callback
		updateDownloadProgress(videoId, totalProgress, "downloading").catch(console.error);
		onProgress?.(totalProgress);
		
		// Log progress every ~5MB
		if (offset % (5 * 1024 * 1024) < CHUNK_SIZE) {
			console.log(`📝 File write progress: ${Math.round(writeProgress)}% (total: ${Math.round(totalProgress)}%)`);
			videoLog.info("FileWrite", `Write progress: ${Math.round(writeProgress)}%`, {
				writtenBytes: offset,
				totalBytes: totalSize,
				writeProgress: Math.round(writeProgress),
				totalProgress: Math.round(totalProgress)
			}, videoId); // ✅ DODAJ
		}
	}

	console.log(`✅ File written successfully in ${Math.ceil(totalSize / CHUNK_SIZE)} chunks`);
	videoLog.success("FileWrite", `File written successfully in ${Math.ceil(totalSize / CHUNK_SIZE)} chunks`, {
		totalSize,
		totalChunks: Math.ceil(totalSize / CHUNK_SIZE)
	}, videoId); // ✅ DODAJ
}

/**
 * Active downloads tracker for pause/cancel functionality
 */
interface ActiveDownload {
	controller: AbortController;
	videoId: string;
}

const activeDownloads = new Map<string, ActiveDownload>();

/**
 * Pause/cancel an active download
 */
export const pauseDownload = async (
	productType: ProductTypeMultimedia,
	productId: string,
	courseClassesId: string
): Promise<void> => {
	const videoId = generateVideoId(productType, productId, courseClassesId);
	const download = activeDownloads.get(videoId);
	
	if (download) {
		console.log(`⏸️ Pausing download for video: ${videoId}`);
		videoLog.warning("Pause", "Pausing download by user", { videoId }, videoId); // ✅ DODAJ
		
		download.controller.abort();
		activeDownloads.delete(videoId);
		
		// Update status to paused
		await updateDownloadProgress(videoId, 0, "paused");
		
		console.log(`💾 Partial file preserved for resume`);
		videoLog.info("Pause", "Partial file preserved for resume", {}, videoId); // ✅ DODAJ
		
		// ✅ SPRAWDŹ czy plik istnieje
        try {
            const video = await getOfflineVideo(videoId);
            if (video?.localFilePath) {
                const stat = await Filesystem.stat({
                    path: video.localFilePath,
                    directory: Directory.Data,
                });
				// ✅ Partial file zostaje na dysku dla resume
                console.log(`💾 Partial file preserved: ${(stat.size / 1024 / 1024).toFixed(2)} MB`);
                videoLog.info("Pause", `Partial file size: ${(stat.size / 1024 / 1024).toFixed(2)} MB`, {
					fileSizeBytes: stat.size,
					fileSizeMB: (stat.size / 1024 / 1024).toFixed(2)
				}, videoId); // ✅ DODAJ
            }
        } catch (error) {
            console.error("Error checking partial file:", error);
            videoLog.error("Pause", "Error checking partial file", {
				error: error instanceof Error ? error.message : String(error)
			}, videoId); // ✅ DODAJ
        }
	} else {
		// No active download, but check if video exists in downloading state (orphaned)
		console.log(`⚠️ No active download found for: ${videoId}`);
		videoLog.warning("Pause", "No active download found - checking for orphaned download", { videoId }, videoId);
		
		const video = await getOfflineVideo(videoId);
		if (video && video.status === "downloading") {
			// This is an orphaned download - fix it
			await updateDownloadProgress(videoId, video.downloadProgress, "paused");
			console.log(`✅ Status changed to paused for orphaned download`);
			videoLog.info("Pause", "Orphaned download status updated to paused", {
				progress: video.downloadProgress
			}, videoId);
		} else if (video) {
			console.log(`ℹ️ Video status is already: ${video.status}`);
		}
	}
};

/**
 * Detect and fix orphaned downloads on app restart
 * Call this from app initialization
 */
export const detectOrphanedDownloads = async (): Promise<void> => {
	console.log("🔍 Checking for interrupted downloads...");
	
	const { getOfflineVideos } = await import("./storage/offlineVideosStorage");
	const videos = await getOfflineVideos();
	
	let fixedCount = 0;
	const updatePromises: Array<Promise<void>> = [];
	
	for (const [videoId, video] of Object.entries(videos)) {
		// If video is marked as downloading but not in activeDownloads
		if (video.status === "downloading" && !activeDownloads.has(videoId)) {
			console.log(`⚠️ Found orphaned download: ${videoId}, changing to paused`);
			
			updatePromises.push(updateDownloadProgress(videoId, video.downloadProgress, "paused"));
			
			videoLog.warning(
				"Recovery",
				"Auto-recovered interrupted download",
				{
					videoId,
					videoName: video.videoName,
					progress: video.downloadProgress
				},
				videoId
			);
			
			fixedCount++;
		}
	}
	
	// Wait for all updates to complete
	await Promise.all(updatePromises);
	
	if (fixedCount > 0) {
		console.log(`✅ Fixed ${fixedCount} interrupted download(s)`);
	} else {
		console.log("✅ No interrupted downloads found");
	}
};

/**
 * Check if a download is currently active
 */
// export const isDownloadActive = (
// 	productId: string,
// 	productType: ProductTypeMultimedia,
// 	courseClassesId: string
// ): boolean => {
// 	const videoId = generateVideoId(productType, productId, courseClassesId);
// 	return activeDownloads.has(videoId);
// };

/**
 * Legacy download function (fallback for servers without Range support)
 * Downloads entire file at once without resume capability
 */
export const downloadVideo = async (
	courseClassesId: string,
	productData: ProductDataMultimedia,
	videoName: string,
	duration: number,
	videoUrl: string,
	resolution: string,
	fileSizeMB: number,
	videoSource: "vimeo_source" | "akademia_source",
	vimeoId?: number,
	onProgress?: (progress: number) => void
): Promise<void> => {
	const { productType, productId } = productData;
	const videoId = generateVideoId(productType, productId, courseClassesId);
	const fileName = `video_${videoId}_${resolution}.mp4`;
	const filePath = `offline_videos/${fileName}`;

	videoLog.info("Download", "Starting legacy download (no Range support)", {
		videoName,
		resolution,
		fileSizeMB,
		videoSource
	}, videoId); // ✅ DODAJ

	// Create AbortController for this download
	const controller = new AbortController();
	activeDownloads.set(videoId, { controller, videoId });

	// Create initial metadata
	const metadata: OfflineVideoMetadata = {
		videoId,
		courseClassesId,
		productData,
		videoName,
		duration,
		videoSource,
		vimeoId,
		originalVideoUrl: videoUrl,
		selectedResolution: resolution,
		downloadUrl: videoUrl,
		fileSizeMB,
		localFilePath: filePath,
		fileName,
		status: "downloading",
		downloadProgress: 0,
		downloadStartedAt: Date.now(),
	};

	await upsertOfflineVideo(metadata);

	try {
		const response = await fetch(videoUrl, {
			signal: controller.signal,
		});
		
		if (!response.ok) {
			videoLog.error("Download", `HTTP error: ${response.status}`, {
				status: response.status,
				statusText: response.statusText,
				url: videoUrl
			}, videoId); // ✅ DODAJ
			throw new Error(`HTTP error! status: ${response.status}`);
		}

		const contentLength = Number(response.headers.get("Content-Length"));
		
		// Ensure directory exists
		try {
			await Filesystem.mkdir({
				path: "offline_videos",
				directory: Directory.Data,
				recursive: true,
			});
		} catch (error) {
			console.log("Directory already exists or error:", error);
		}

		// Download with progress tracking using streaming
		if (contentLength && response.body) {
			videoLog.info("Download", "Starting streaming download with progress", {
				contentLength,
				contentLengthMB: (contentLength / 1024 / 1024).toFixed(2)
			}, videoId); // ✅ DODAJ
			
			const reader = response.body.getReader();
			let receivedLength = 0;
			const chunks: Array<Uint8Array> = [];
			let lastProgressUpdate = 0;

			// Read the response stream (0-90% of total progress)
			/* eslint-disable no-await-in-loop */
			while (true) {
				const { done, value } = await reader.read();

				if (done) break;

				chunks.push(value);
				receivedLength += value.length;

				// Map download progress to 0-90% of total
				const downloadProgress = (receivedLength / contentLength) * 100;
				const totalProgress = downloadProgress * 0.9;
				
				// Update progress every 1% to reduce storage writes
				if (Math.floor(totalProgress) - lastProgressUpdate >= 1) {
					lastProgressUpdate = Math.floor(totalProgress);
					updateDownloadProgress(videoId, totalProgress, "downloading").catch(console.error);
					onProgress?.(totalProgress);
					console.log(`📥 Download progress: ${Math.round(downloadProgress)}% (total: ${Math.round(totalProgress)}%)`);
					videoLog.info("Download", `Download progress: ${Math.round(downloadProgress)}%`, {
						receivedBytes: receivedLength,
						totalBytes: contentLength,
						progress: Math.round(totalProgress)
					}, videoId); // ✅ DODAJ
				}
			}
			/* eslint-enable no-await-in-loop */

			// Create blob from chunks
			const blob = new Blob(chunks as Array<BlobPart>, { type: "video/mp4" });
			
			// Clear chunks array to free memory
			chunks.length = 0;

			// Write file in chunks (90-100% of total progress)
			await writeFileInChunks(blob, filePath, videoId, onProgress);

			console.log(`✅ Video downloaded successfully: ${fileName}`);
		} else {
			// Fallback: Download entire blob at once (no progress tracking)
			console.warn("⚠️ No Content-Length header, downloading without progress");
			videoLog.warning("Download", "No Content-Length header - downloading without progress", {
				url: videoUrl
			}, videoId); // ✅ DODAJ
			
			const blob = await response.blob();

			// Assume download is done, show 90% then write file
			updateDownloadProgress(videoId, 90, "downloading").catch(console.error);
			onProgress?.(90);
			
			await writeFileInChunks(blob, filePath, videoId, onProgress);

			console.log(`✅ Video downloaded successfully (no progress): ${fileName}`);
		}

		// Update metadata to completed
		await updateDownloadProgress(videoId, 100, "completed");
		onProgress?.(100);
		videoLog.success("Download", "Download completed successfully", {
			fileName,
			fileSizeMB
		}, videoId); // ✅ DODAJ
		
	} catch (error) {
		// Check if this was an intentional abort (pause)
		if (error instanceof Error && error.name === 'AbortError') {
			console.log('⏸️ Download paused by user');
			videoLog.info("Download", "Download paused by user (AbortError)", {}, videoId); // ✅ DODAJ
			// Status already set to "paused" by pauseDownload()
			return;
		}
		
		// Real error
		console.error("Error downloading video:", error);
		videoLog.error("Download", "Download failed", {
			error: error instanceof Error ? error.message : String(error),
			errorName: error instanceof Error ? error.name : undefined,
			stack: error instanceof Error ? error.stack : undefined
		}, videoId); // ✅ DODAJ
		
		await updateDownloadProgress(videoId, 0, "failed");
		throw error;
	} finally {
		// Clean up active download tracker
		activeDownloads.delete(videoId);
	}
};

/**
 * Helper: Convert ArrayBuffer to Base64
 */
function arrayBufferToBase64(buffer: ArrayBuffer): Promise<string> {
	return new Promise((resolve, reject) => {
		const blob = new Blob([buffer]);
		const reader = new FileReader();
		reader.onloadend = (): void => {
			if (typeof reader.result === 'string') {
				const base64 = reader.result.split(',')[1];
				resolve(base64 || '');
			} else {
				reject(new Error('Failed to convert ArrayBuffer to base64'));
			}
		};
		reader.onerror = (): void => {
			reject(new Error('FileReader error'));
		};
		reader.readAsDataURL(blob);
	});
}

/**
 * Download video with Range Request support for resume capability
 * This allows resuming downloads after network interruptions or app restarts
 */
export const downloadVideoWithResume = async (
	// workshopId: string,
	courseClassesId: string,
	productData: ProductDataMultimedia,
	videoName: string,
	duration: number,
	videoUrl: string,
	resolution: string,
	fileSizeMB: number,
	videoSource: "vimeo_source" | "akademia_source",
	vimeoId?: number,
	onProgress?: (progress: number) => void
): Promise<void> => {
	const { productType, productId } = productData;
	const videoId = generateVideoId(productType, productId, courseClassesId);
	const fileName = `video_${videoId}_${resolution}.mp4`;
	const filePath = `offline_videos/${fileName}`;
	
	const NETWORK_CHUNK_SIZE = 2 * 1024 * 1024; // 2MB per network request
	const WRITE_CHUNK_SIZE = 512 * 1024; // 512KB per write (Android bridge safe)
	
	videoLog.info("Download", "Starting download with Range support", {
		videoName,
		resolution,
		fileSizeMB,
		videoSource,
		networkChunkSizeMB: (NETWORK_CHUNK_SIZE / 1024 / 1024).toFixed(2),
		writeChunkSizeKB: (WRITE_CHUNK_SIZE / 1024).toFixed(0)
	}, videoId);
	
	// Create AbortController for this download
	const controller = new AbortController();
	activeDownloads.set(videoId, { controller, videoId });

	// Determine Range support based on video source
	const supportsRange = videoSource === 'vimeo_source' || videoSource === 'akademia_source';
	
	if (!supportsRange) {
		console.warn('⚠️ Unknown video source, falling back to standard download');
		videoLog.warning("Download", "Unknown video source - falling back to standard download", {
			videoSource
		}, videoId);
		
		activeDownloads.delete(videoId);
		return downloadVideo(
			courseClassesId,
			productData,
			videoName,
			duration,
			videoUrl,
			resolution,
			fileSizeMB,
			videoSource,
			vimeoId,
			onProgress
		);
	}

	console.log(`✅ ${videoSource} supports Range requests - using resume-capable download`);
	videoLog.success("Download", `${videoSource} supports Range requests`, { videoSource }, videoId); // ✅ DODAJ

	// Get file size with HEAD request
	let totalSize = 0;
	try {
		const headResponse = await fetch(videoUrl, { method: 'HEAD' });
		const contentLength = headResponse.headers.get('Content-Length');
		totalSize = contentLength ? Number(contentLength) : 0;
		console.log(`📊 File size: ${(totalSize / 1024 / 1024).toFixed(2)} MB`);
		videoLog.info("Download", `File size retrieved: ${(totalSize / 1024 / 1024).toFixed(2)} MB`, {
			totalSize,
			totalSizeMB: (totalSize / 1024 / 1024).toFixed(2)
		}, videoId); // ✅ DODAJ
	} catch (error) {
		console.error('Error getting file size:', error);
		videoLog.error("Download", "Error getting file size from HEAD request", {
			error: error instanceof Error ? error.message : String(error)
		}, videoId); // ✅ DODAJ
		
		// Fallback if HEAD fails
		activeDownloads.delete(videoId);
		return downloadVideo(
			courseClassesId,
			productData,
			videoName,
			duration,
			videoUrl,
			resolution,
			fileSizeMB,
			videoSource,
			vimeoId,
			onProgress
		);
	}

	// Create initial metadata
	const metadata: OfflineVideoMetadata = {
		videoId,
		courseClassesId,
		productData,
		videoName,
		duration,
		videoSource,
		vimeoId,
		originalVideoUrl: videoUrl,
		selectedResolution: resolution,
		downloadUrl: videoUrl,
		fileSizeMB,
		localFilePath: filePath,
		fileName,
		status: "downloading",
		downloadProgress: 0,
		downloadStartedAt: Date.now(),
	};

	await upsertOfflineVideo(metadata);

	// Ensure directory exists
	try {
		await Filesystem.mkdir({
			path: "offline_videos",
			directory: Directory.Data,
			recursive: true,
		});
	} catch (error) {
		console.log("Directory already exists or error:", error);
	}

	// Load checkpoint if exists (resume previous download)
	const checkpoint = await loadCheckpoint(videoId);
	const downloadedBytes = checkpoint?.downloadedBytes || 0;
	
	if (checkpoint && checkpoint.downloadedBytes > 0) {
		console.log(`🔄 Resuming download from ${(downloadedBytes / 1024 / 1024).toFixed(2)} MB`);
		videoLog.info("Resume", `Resuming from checkpoint: ${(downloadedBytes / 1024 / 1024).toFixed(2)} MB`, {
			downloadedBytes,
			downloadedMB: (downloadedBytes / 1024 / 1024).toFixed(2),
			totalSize,
			resumeProgress: ((downloadedBytes / totalSize) * 100).toFixed(1)
		}, videoId); // ✅ DODAJ
		
		// Update progress immediately
		const resumeProgress = (downloadedBytes / totalSize) * 90; // Map to 0-90%
		onProgress?.(resumeProgress);
		await updateDownloadProgress(videoId, resumeProgress, "downloading");
	} else {
		videoLog.info("Download", "No checkpoint found - starting fresh", {}, videoId); // ✅ DODAJ
	}

	try {
		let currentPosition = downloadedBytes;
		let lastProgressUpdate = 0;

		// Download in chunks with Range requests
		while (currentPosition < totalSize) {
			const end = Math.min(currentPosition + NETWORK_CHUNK_SIZE - 1, totalSize - 1);
			
			console.log(`📥 Downloading chunk: ${(currentPosition / 1024 / 1024).toFixed(2)} - ${(end / 1024 / 1024).toFixed(2)} MB`);
			videoLog.info("Download", `Downloading chunk: ${(currentPosition / 1024 / 1024).toFixed(2)} - ${(end / 1024 / 1024).toFixed(2)} MB`, {
				currentPosition,
				end,
				chunkSize: end - currentPosition + 1,
				progress: ((currentPosition / totalSize) * 100).toFixed(1)
			}, videoId); // ✅ DODAJ
			
			// Fetch chunk with Range header
			const response = await fetch(videoUrl, {
				headers: {
					'Range': `bytes=${currentPosition}-${end}`
				},
				signal: controller.signal
			});
			
			if (response.status !== 206 && response.status !== 200) {
				videoLog.error("Download", `Unexpected response status: ${response.status}`, {
					status: response.status,
					statusText: response.statusText,
					expectedStatus: "206 or 200"
				}, videoId); // ✅ DODAJ
				throw new Error(`Unexpected response status: ${response.status}`);
			}
			
			// Get chunk data
			const chunkArrayBuffer = await response.arrayBuffer();
			const chunkSize = chunkArrayBuffer.byteLength;
			
			// Write chunk to file in smaller pieces (for Android bridge)
			let chunkOffset = 0;
			while (chunkOffset < chunkSize) {
				const writeEnd = Math.min(chunkOffset + WRITE_CHUNK_SIZE, chunkSize);
				const writeChunk = chunkArrayBuffer.slice(chunkOffset, writeEnd);
				const base64Chunk = await arrayBufferToBase64(writeChunk);
				
				// First write: create file, subsequent: append
				if (currentPosition === 0 && chunkOffset === 0) {
					await Filesystem.writeFile({
						path: filePath,
						data: base64Chunk,
						directory: Directory.Data,
					});
				} else {
					await Filesystem.appendFile({
						path: filePath,
						data: base64Chunk,
						directory: Directory.Data,
					});
				}
				
				chunkOffset = writeEnd;
			}
			
			// Update actual position AFTER completing the write loop
			currentPosition = end + 1;
			
			// Calculate progress (0-90% for download, 90-100% reserved for finalization)
			const downloadProgress = (currentPosition / totalSize) * 100;
			const totalProgress = downloadProgress * 0.9;
			
			// Update progress every 1% to reduce storage writes
			if (Math.floor(totalProgress) - lastProgressUpdate >= 1) {
				lastProgressUpdate = Math.floor(totalProgress);
				onProgress?.(totalProgress);
				await updateDownloadProgress(videoId, totalProgress, "downloading");
				videoLog.info("Download", `Progress updated: ${Math.round(totalProgress)}%`, {
					downloadedBytes: currentPosition,
					totalSize,
					progress: Math.round(totalProgress)
				}, videoId); // ✅ DODAJ
			}
			
			// Save checkpoint every 10MB
			if (currentPosition % (10 * 1024 * 1024) < NETWORK_CHUNK_SIZE) {
				await saveCheckpoint(videoId, {
					videoId,
					downloadedBytes: currentPosition,
					totalSize,
					lastUpdate: Date.now(),
					filePath
				});
				videoLog.info("Checkpoint", `Checkpoint saved at ${(currentPosition / 1024 / 1024).toFixed(2)} MB`, {
					downloadedBytes: currentPosition,
					downloadedMB: (currentPosition / 1024 / 1024).toFixed(2),
					progress: ((currentPosition / totalSize) * 100).toFixed(1)
				}, videoId); // ✅ DODAJ
			}
		}

		console.log(`✅ Download completed: ${fileName}`);
		videoLog.success("Download", `Download completed: ${fileName}`, {
			fileName,
			totalSize,
			totalSizeMB: (totalSize / 1024 / 1024).toFixed(2)
		}, videoId); // ✅ DODAJ
		
		// Finalization (90-100%)
		onProgress?.(95);
		await updateDownloadProgress(videoId, 95, "downloading");
		
		// Verify file size
		try {
			const stat = await Filesystem.stat({
				path: filePath,
				directory: Directory.Data,
			});
			console.log(`📊 Final file size: ${(stat.size / 1024 / 1024).toFixed(2)} MB`);
			videoLog.info("Download", `Final file size verified: ${(stat.size / 1024 / 1024).toFixed(2)} MB`, {
				fileSizeBytes: stat.size,
				fileSizeMB: (stat.size / 1024 / 1024).toFixed(2),
				expectedSize: totalSize,
				match: stat.size === totalSize
			}, videoId); // ✅ DODAJ
		} catch (error) {
			console.warn('Could not verify file size:', error);
			videoLog.warning("Download", "Could not verify final file size", {
				error: error instanceof Error ? error.message : String(error)
			}, videoId); // ✅ DODAJ
		}
		
		// Clear checkpoint and mark as completed
		await clearCheckpoint(videoId);
		videoLog.info("Checkpoint", "Checkpoint cleared after successful download", {}, videoId); // ✅ DODAJ
		
		await updateDownloadProgress(videoId, 100, "completed");
		onProgress?.(100);
		
	} catch (error) {
		// Check if this was an intentional abort (pause)
		if (error instanceof Error && error.name === 'AbortError') {
			console.log('⏸️ Download paused by user');
			videoLog.info("Download", "Download paused by user (AbortError)", {}, videoId); // ✅ DODAJ
			
			// Save final checkpoint
			await saveCheckpoint(videoId, {
				videoId,
				downloadedBytes: downloadedBytes,
				totalSize,
				lastUpdate: Date.now(),
				filePath
			});
			// Status already set to "paused" by pauseDownload()
			throw new Error('DOWNLOAD_PAUSED');
		}
		
		// Network error or other error - save checkpoint for resume
		console.error("❌ Download error:", error);
		videoLog.error("Download", "Download failed with error", {
			error: error instanceof Error ? error.message : String(error),
			errorName: error instanceof Error ? error.name : undefined,
			downloadedBytes,
			downloadedMB: (downloadedBytes / 1024 / 1024).toFixed(2),
			stack: error instanceof Error ? error.stack : undefined
		}, videoId); // ✅ DODAJ
		
		// ✅ DODAJ: Cleanup partial file przy permanent failure
		// (opcjonalnie - możesz zachować dla retry)
		const shouldCleanupOnError = false; // Zmień na true jeśli chcesz cleanup
		
		if (shouldCleanupOnError) {
			try {
				await Filesystem.deleteFile({
					path: filePath,
					directory: Directory.Data,
				});
				console.log(`🗑️ Deleted partial file after error`);
				videoLog.success("Cancel", `Deleted partial file after error: ${filePath}`, {
					filePath
				}, videoId); // ✅ DODAJ
			} catch (cleanupError) {
				console.log("No partial file to cleanup:", cleanupError);
				videoLog.warning("Cancel", "No partial file to cleanup or error", {
					error: cleanupError instanceof Error ? cleanupError.message : String(cleanupError)
				}, videoId); // ✅ DODAJ
			}
		}
		
		await saveCheckpoint(videoId, {
			videoId,
			downloadedBytes: downloadedBytes,
			totalSize,
			lastUpdate: Date.now(),
			filePath
		});
		await updateDownloadProgress(videoId, (downloadedBytes / totalSize) * 90, "paused");
		throw error;
	} finally {
		// Clean up active download tracker
		activeDownloads.delete(videoId);
	}
};

/**
 * Resume a paused download
 */
export const resumeDownload = async (
	productType: ProductTypeMultimedia,
	productId: string,
	courseClassesId: string,
	onProgress?: (progress: number) => void
): Promise<void> => {
	const videoId = generateVideoId(productType, productId, courseClassesId);
	const { getOfflineVideo } = await import("./storage/offlineVideosStorage");
	const video = await getOfflineVideo(videoId);
	
	if (!video) {
		videoLog.error("Resume", "Video metadata not found", { videoId }, videoId); // ✅ DODAJ
		throw new Error('Video not found');
	}
	
	if (video.status !== 'paused') {
		console.warn('Video is not in paused state');
		videoLog.warning("Resume", `Video is not in paused state - current status: ${video.status}`, {
			currentStatus: video.status,
			videoId
		}, videoId); // ✅ DODAJ
		return;
	}
	
	videoLog.info("Resume", "Resuming paused download", {
		videoName: video.videoName,
		currentProgress: video.downloadProgress
	}, videoId); // ✅ DODAJ
	
	// Resume download using stored metadata
	await downloadVideoWithResume(
		video.courseClassesId,
		video.productData,
		video.videoName,
		video.duration,
		video.downloadUrl,
		video.selectedResolution,
		video.fileSizeMB,
		video.videoSource,
		video.vimeoId,
		onProgress
	);
};

/**
 * Get local video as Blob URL for playback
 * Works with video players in WebView
 * NOTE: Only use for files that can fit in memory (< 100MB recommended)
 */
// export const getLocalVideoBlobUrl = async (
// 	workshopId: string,
// 	courseClassesId: string
// ): Promise<string | null> => {
// 	if (!isNativePlatform()) {
// 		videoLog.warning("Playback", "Web platform - offline not supported", {}); // ✅ DODAJ
// 		return null;
// 	}

// 	const videoId = generateVideoId(workshopId, courseClassesId);
// 	const { getOfflineVideo } = await import("./storage/offlineVideosStorage");
// 	const video = await getOfflineVideo(videoId);

// 	if (!video || video.status !== "completed") {
// 		console.log("❌ Video not found or not completed");
// 		videoLog.warning("Playback", "Local video not found or not completed", {
// 			found: !!video,
// 			status: video?.status
// 		}, videoId); // ✅ DODAJ
// 		return null;
// 	}

// 	try {
// 		// Check file size - prevent OOM for large files
// 		const stat = await Filesystem.stat({
// 			path: video.localFilePath,
// 			directory: Directory.Data,
// 		});
		
// 		const fileSizeBytes = stat.size;
// 		const fileSizeMB = fileSizeBytes / (1024 * 1024);
		
// 		console.log(`📊 File size: ${fileSizeMB.toFixed(2)} MB`);
// 		videoLog.info("Playback", `File size: ${fileSizeMB.toFixed(2)} MB`, {
// 			fileSizeBytes,
// 			fileSizeMB: fileSizeMB.toFixed(2)
// 		}, videoId); // ✅ DODAJ
		
// 		// Limit to ~80MB to prevent OOM (leaving some headroom)
// 		const MAX_FILE_SIZE_MB = 80;
// 		if (fileSizeMB > MAX_FILE_SIZE_MB) {
// 			console.warn(`⚠️ File too large (${fileSizeMB.toFixed(2)} MB). Maximum supported: ${MAX_FILE_SIZE_MB} MB`);
// 			videoLog.warning("Playback", `File too large for Blob URL - using file URI instead`, {
// 				fileSizeMB: fileSizeMB.toFixed(2),
// 				maxSizeMB: MAX_FILE_SIZE_MB
// 			}, videoId); // ✅ DODAJ
			
// 			// Still try file:// URI as fallback
// 			const fileUri = await Filesystem.getUri({
// 				path: video.localFilePath,
// 				directory: Directory.Data,
// 			});
// 			const webViewUri = Capacitor.convertFileSrc(fileUri.uri);
// 			console.log("🔄 Using file URI fallback:", webViewUri);
// 			videoLog.info("Playback", "Using file URI fallback", { 
// 				webViewUri 
// 			}, videoId); // ✅ DODAJ
// 			return webViewUri;
// 		}

// 		// Read file as base64 (original working implementation)
// 		console.log("📂 Reading file from:", video.localFilePath);
// 		videoLog.info("Playback", "Reading file for Blob URL creation", {
// 			filePath: video.localFilePath
// 		}, videoId); // ✅ DODAJ
		
// 		const fileData = await Filesystem.readFile({
// 			path: video.localFilePath,
// 			directory: Directory.Data,
// 		});

// 		// Convert base64 to blob
// 		const base64Data = fileData.data;
		
// 		console.log("🔄 Converting to Blob URL...");
// 		// Use fetch to convert base64 to blob (more efficient for large files)
// 		const response = await fetch(`data:video/mp4;base64,${base64Data}`);
// 		const blob = await response.blob();

// 		// Create and return blob URL
// 		const blobUrl = URL.createObjectURL(blob);
// 		console.log("✅ Created Blob URL for video playback");
// 		videoLog.success("Playback", "Blob URL created successfully", {
// 			blobUrl
// 		}, videoId); // ✅ DODAJ
// 		return blobUrl;
// 	} catch (error) {
// 		console.error("❌ Error creating blob URL from local video:", error);
// 		videoLog.error("Playback", "Error creating Blob URL", {
// 			error: error instanceof Error ? error.message : String(error),
// 			stack: error instanceof Error ? error.stack : undefined
// 		}, videoId); // ✅ DODAJ
		
// 		// Try file:// URI as last resort fallback
// 		try {
// 			const fileUri = await Filesystem.getUri({
// 				path: video.localFilePath,
// 				directory: Directory.Data,
// 			});
// 			const webViewUri = Capacitor.convertFileSrc(fileUri.uri);
// 			console.log("🔄 Fallback to file URI:", webViewUri);
// 			videoLog.info("Playback", "Fallback to file URI after Blob error", {
// 				webViewUri
// 			}, videoId); // ✅ DODAJ
// 			return webViewUri;
// 		} catch (fallbackError) {
// 			console.error("❌ Fallback also failed:", fallbackError);
// 			videoLog.error("Playback", "File URI fallback also failed", {
// 				error: fallbackError instanceof Error ? fallbackError.message : String(fallbackError)
// 			}, videoId); // ✅ DODAJ
// 			return null;
// 		}
// 	}
// };

/**
 * Get local video file URI for playback (alternative to Blob URL)
 * Returns platform-appropriate URI for WebView playback
 */
export const getLocalVideoUri = async (
	productType: ProductTypeMultimedia,
	productId: string,
	courseClassesId: string
): Promise<string | null> => {
	const videoId = generateVideoId(productType, productId, courseClassesId);
	const { getOfflineVideo } = await import("./storage/offlineVideosStorage");
	const video = await getOfflineVideo(videoId);

	console.log("🔍 [getLocalVideoUri] Looking for video:", {
		videoId,
		found: !!video,
		status: video?.status,
		platform: Capacitor.getPlatform()
	});

	if (!video || video.status !== "completed") {
		videoLog.warning("Playback", "Local video not found or not completed", {
			found: !!video,
			status: video?.status,
			platform: Capacitor.getPlatform()
		}, videoId);
		return null;
	}

	try {
		const fileUri = await Filesystem.getUri({
			path: video.localFilePath,
			directory: Directory.Data,
		});
		
		// Check platform
		const platform = Capacitor.getPlatform();
		videoLog.info("Playback", `Platform detected: ${platform}`, { platform }, videoId); // ✅ DODAJ
		
		if (platform === 'android') {
			// Android: Manual URL construction (works better for Android)
			const rawPath = fileUri.uri.replace(/^file:\/\//, ""); // bez "file://"
			const webViewUri = `http://localhost/_capacitor_file_/${rawPath.replace(/^\/+/, "")}`;
			console.log("✅ Android WebView URI (manual):", webViewUri);
			videoLog.success("Playback", "Android WebView URI generated", {
				fileUri: fileUri.uri,
				webViewUri
			}, videoId);
			return webViewUri;
		} else if (platform === 'ios') {
			// iOS: Use Capacitor.convertFileSrc (generates capacitor:// URL)
			const webViewUri = Capacitor.convertFileSrc(fileUri.uri);
			console.log("✅ iOS WebView URI (Capacitor):", webViewUri);
			videoLog.success("Playback", "iOS WebView URI generated", {
				fileUri: fileUri.uri,
				webViewUri
			}, videoId);
			return webViewUri;
		} else {
			// Web platform - konwertuj na blob URL z cache
			try {
				// Sprawdź cache
				const cachedUrl = videoBlobCache.get(videoId);
				if (cachedUrl) {
					console.log("✅ [Web] Using cached blob URL:", cachedUrl);
					videoLog.success("Playback", "Web platform - using cached blob URL", {
						blobUrl: cachedUrl,
						videoId
					}, videoId);
					return cachedUrl;
				}
				
				console.log("🔄 [Web] Creating new blob URL (not in cache)...");
				
				// Przeczytaj plik z IndexedDB/Filesystem
				const fileData = await Filesystem.readFile({
					path: video.localFilePath,
					directory: Directory.Data,
				});
				
				console.log("📁 [Web] File read successfully, creating blob...");
				
				// Konwertuj base64 na blob
				let blob: Blob;
				if (typeof fileData.data === 'string') {
					// Base64 string - konwertuj na blob
					const response = await fetch(`data:video/mp4;base64,${fileData.data}`);
					blob = await response.blob();
				} else {
					// Już jest Blob
					blob = fileData.data;
				}
				
				const blobUrl = URL.createObjectURL(blob);
				
				// Zapisz w cache
				videoBlobCache.set(videoId, blobUrl);
				
				console.log("✅ Web platform - created and cached blob URL:", blobUrl);
				videoLog.success("Playback", "Web platform - created and cached blob URL", {
					blobUrl,
					fileSize: blob.size,
					fileSizeMB: (blob.size / 1024 / 1024).toFixed(2),
					cacheSize: videoBlobCache.size
				}, videoId);
				
				return blobUrl;
			} catch (error) {
				console.error("❌ [Web] Error creating blob URL:", error);
				videoLog.error("Playback", "Error creating blob URL for web", {
					error: error instanceof Error ? error.message : String(error),
					stack: error instanceof Error ? error.stack : undefined
				}, videoId);
				return null;
			}
		}
	} catch (error) {
		console.error("Error getting local video URI:", error);
		videoLog.error("Playback", "Error getting local video URI", {
			error: error instanceof Error ? error.message : String(error),
			stack: error instanceof Error ? error.stack : undefined
		}, videoId); // ✅ DODAJ
		return null;
	}
};

/**
 * Calculate video file size from Vimeo progressive video
 */
export const calculateVideoSizeMB = (video: ProgressiveVideo): number => {
	return Math.round((video.size / 1024 / 1024) * 100) / 100;
};

/**
 * Get best quality progressive video from Vimeo response
 */
export const getBestQualityProgressive = (
	progressiveVideos: Array<ProgressiveVideo>
): ProgressiveVideo | null => {
	if (!progressiveVideos || progressiveVideos.length === 0) {
		return null;
	}
	const sorted = [...progressiveVideos].sort((a, b) => b.height - a.height);
	return sorted[0] ?? null;
};

/**
 * Cancel download and cleanup all files
 */
export const cancelDownload = async (
	productType: ProductTypeMultimedia,
	productId: string,
	courseClassesId: string
): Promise<void> => {
	const videoId = generateVideoId(productType, productId, courseClassesId);
	const download = activeDownloads.get(videoId);
	
	videoLog.warning("Cancel", "Canceling download", { videoId }, videoId); // ✅ DODAJ
	
	// Abort active download if exists
	if (download) {
		console.log(`❌ Canceling download for video: ${videoId}`);
		download.controller.abort();
		activeDownloads.delete(videoId);
		videoLog.info("Cancel", "Active download aborted", {}, videoId); // ✅ DODAJ
	}
	
	// Get video metadata
	const video = await getOfflineVideo(videoId);
	
	// Delete partial file
	if (video?.localFilePath) {
		try {
			await Filesystem.deleteFile({
				path: video.localFilePath,
				directory: Directory.Data,
			});
			console.log(`🗑️ Deleted partial file: ${video.localFilePath}`);
			videoLog.success("Cancel", `Deleted partial file: ${video.localFilePath}`, {
				filePath: video.localFilePath
			}, videoId); // ✅ DODAJ
		} catch (error) {
			console.log("No partial file to delete or error:", error);
			videoLog.warning("Cancel", "No partial file to delete or error", {
				error: error instanceof Error ? error.message : String(error)
			}, videoId); // ✅ DODAJ
		}
	}
	
	// Delete checkpoint
	await clearCheckpoint(videoId);
	videoLog.info("Cancel", "Checkpoint cleared", {}, videoId); // ✅ DODAJ
	
	// Delete metadata
	const { removeOfflineVideo } = await import("./storage/offlineVideosStorage");
	await removeOfflineVideo(videoId);
	
	console.log(`✅ Download canceled and cleaned up: ${videoId}`);
	videoLog.success("Cancel", "Download canceled and cleaned up successfully", {}, videoId); // ✅ DODAJ
};


